import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export type LeagueTier = "bronze" | "silver" | "gold" | "platinum" | "diamond";

export interface MyLeagueStanding {
  season_id: string;
  season_number: number;
  tier: LeagueTier;
  weekly_xp: number;
  ends_at: string;
  my_rank: number | null;
  total_in_tier: number;
  promotion_threshold: number;
  demotion_threshold: number;
}

export interface LeagueLeaderboardRow {
  user_id: string;
  display_name: string | null;
  weekly_xp: number;
  rank: number;
  is_self: boolean;
  in_promotion_zone: boolean;
  in_demotion_zone: boolean;
}

export const TIER_META: Record<LeagueTier, { label: string; color: string; gradient: string; emoji: string }> = {
  bronze:   { label: "Bronze",   color: "text-amber-600",  gradient: "from-amber-700 to-amber-500",   emoji: "🥉" },
  silver:   { label: "Silver",   color: "text-slate-300",  gradient: "from-slate-400 to-slate-200",   emoji: "🥈" },
  gold:     { label: "Gold",     color: "text-yellow-400", gradient: "from-yellow-500 to-yellow-300", emoji: "🥇" },
  platinum: { label: "Platinum", color: "text-cyan-300",   gradient: "from-cyan-500 to-cyan-300",     emoji: "💎" },
  diamond:  { label: "Diamond",  color: "text-violet-300", gradient: "from-violet-500 to-fuchsia-400", emoji: "👑" },
};

export function useMyLeagueStanding() {
  const { user } = useAuth();
  const [data, setData] = useState<MyLeagueStanding | null>(null);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    const { data: rows } = await supabase.rpc("get_my_league_standing");
    setData(((rows) || [])[0] || null);
    setLoading(false);
  }, [user]);

  useEffect(() => { refetch(); }, [refetch]);
  return { standing: data, loading, refetch };
}

export function useLeagueLeaderboard() {
  const { user } = useAuth();
  const [rows, setRows] = useState<LeagueLeaderboardRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    supabase.rpc("get_league_leaderboard").then(({ data }) => {
      setRows((data) || []);
      setLoading(false);
    });
  }, [user]);

  return { rows, loading };
}
